import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { AddSubsceneTool } from 'js/src/agent/tools/AddSubsceneTool';
import { LintVideoTool } from 'js/src/agent/tools/LintVideoTool';
import { SetSceneDurationTool } from 'js/src/agent/tools/SetSceneDurationTool';
import { SetTextTool } from 'js/src/agent/tools/SetTextTool';

export const textVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.textVideoBasics,
    description: 'Load before creating or editing text scene groups (scenes with no narration).',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Text Video Basics

For scenes in a text-only video (or text scene groups mixed into a larger video),
where on-screen text carries the whole message.

## One thought per scene
With no narration the video behaves like a slideshow: the viewer must be able to
take in each scene's complete thought from the screen alone. Give each thought its
own scene and don't stack several fragments that don't read as one sentence. You tend
to create overly complex scenes (eyebrow + headline + subtitle) which isn't great. Most scenes
should contain a short-sentence worth of content and should read continuously without eyebrows/subtitles.
That's fine for the occasional title scene but reads like a presentation slide.

## Media scenes first
Prefer a media scene whenever relevant, visually coherent media exists; fall back
to a text-only design when nothing suitable turns up and the text is strong enough
to carry the scene on its own.

## Subscenes: the pacing tool
A subscene is a step within one scene: the visuals (media, background, layout) stay
on screen while one set of text animates out and the next in. It's how a longer
thought splits into short, snappy steps without jump-cutting the visuals.

Two ways to create them:
- \`${AddSubsceneTool.toolName}\`, then \`${SetTextTool.toolName}\` targeting the new step via \`subscene_index\`. The
  new subscene starts as a copy of the last one, so its text must be updated.
- Or in one call: \`${SetTextTool.toolName}\` with \`||\` separating the chunks — it creates the
  subscenes and distributes the text (and any \`**highlights**\`) in one step.

Split at natural break points only — a comma (unless it separates list items),
a conjunction, a clause boundary — never mid-phrase.

## Durations
- The scene duration is automatically set by the system to a value where the user can read
  all of the text. (\`${SetSceneDurationTool.toolName}\` with mode \`"auto"\`). Use \`"manual"\` only for
  a deliberate hold or to speed up/slowdown.
- \`${LintVideoTool.toolName}\`'s \`scene_reading_time\` only catches scenes too *short* to read.
  Keeping scenes from running long is on you. If a scene sits past ~3-4 seconds,
  that usually means too much text: split it into subscenes or multiple scenes.
- Hit a duration target with **more scenes and more content**, never by stretching
  scene durations past their reading time. Overly long scenes drag!

## Text carries everything — write it to be read
With no narration, every scene's text must stand entirely on its own.  The viewer
gets the point with zero outside context. Trim for brevity, never for meaning.
- A scene's text should read as a sentence or one complete, flowing thought.
  Headings are fine, but a headline plus disconnected fragments is disjointed — write
  copy that flows when read top to bottom.
- If the copy only fits the design as fragments, the design is wrong for that
  content — switch designs rather than degrading the words.`;
    },
};
